import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import type { TeamsSearch } from '@/app/routes/teams.index';
import { AdminLayout } from '@/app/shell/admin-layout';
import { type AdminTeam, type AdminTeamsQuery, useAdminTeams } from '@/shared/api/admin-teams';
import { fmtBytes, fmtDate } from '@/shared/lib/format';
import { useDebounced } from '@/shared/lib/use-debounced';
import {
  Badge,
  CopyId,
  FilterChip,
  PageHeader,
  Pagination,
  SearchInput,
  SelectFilter,
  TableShell,
  TableStateRow,
  TD,
  TH,
  THEAD,
  TR,
  WIDE,
} from '@/shared/ui/kit';

const PAGE_SIZE = 50;
const PLAN_FILTERS = ['free', 'personal_pro', 'team', 'enterprise'] as const;
const STATUS_FILTERS = ['free', 'trial', 'active', 'past_due', 'cancelled', 'lifetime'] as const;

export function TeamsListPage() {
  const search = useSearch({ from: '/teams/' });
  const navigate = useNavigate({ from: '/teams/' });
  const setSearch = (patch: Partial<TeamsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }), replace: true });

  const [text, setText] = useState(search.q ?? '');
  const debounced = useDebounced(text.trim(), 300);
  useEffect(() => {
    if ((search.q ?? '') !== debounced) setSearch({ q: debounced || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const offset = search.offset ?? 0;
  const kind = search.kind ?? 'regular';
  const query: AdminTeamsQuery = { limit: PAGE_SIZE, offset };
  if (search.q) query.search = search.q;
  if (kind !== 'all') query.kind = kind;
  if (search.plan) query.billingPlan = search.plan;
  if (search.status) query.subscriptionStatus = search.status;
  if (search.archived) query.includeArchived = true;

  const { data, isLoading, isFetching, error } = useAdminTeams(query);

  return (
    <AdminLayout>
      <PageHeader
        title="Teams"
        description="Shared teams by default (personal workspaces behind the Personal filter). Open a team to grant or change its plan, see members, projects and storage."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput value={text} onChange={setText} placeholder="Team name or slug" />
        <div className="flex gap-1.5">
          <FilterChip label="Shared" active={kind === 'regular'} onClick={() => setSearch({ kind: undefined })} />
          <FilterChip label="Personal" active={kind === 'personal'} onClick={() => setSearch({ kind: 'personal' })} />
          <FilterChip label="All" active={kind === 'all'} onClick={() => setSearch({ kind: 'all' })} />
        </div>
        <SelectFilter label="Plan" value={search.plan} options={PLAN_FILTERS} onChange={(v) => setSearch({ plan: v })} />
        <SelectFilter
          label="Status"
          value={search.status}
          options={STATUS_FILTERS}
          onChange={(v) => setSearch({ status: v as TeamsSearch['status'] })}
        />
        <FilterChip
          label="Include archived"
          active={Boolean(search.archived)}
          onClick={() => setSearch({ archived: search.archived ? undefined : '1' })}
        />
        <div className="ml-auto text-xs text-neutral-500" aria-live="polite">
          {isFetching ? 'Loading…' : data ? `${data.total.toLocaleString()} teams` : null}
        </div>
      </div>

      <TableShell>
        <thead className={THEAD}>
          <tr>
            <th className={TH}>Team</th>
            <th className={TH}>Plan</th>
            <th className={`${TH} text-right`}>Members</th>
            <th className={`${TH} ${WIDE} text-right`}>Storage</th>
            <th className={`${TH} ${WIDE}`}>Paid until</th>
            <th className={`${TH} ${WIDE}`}>Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          <TableStateRow
            colSpan={6}
            loading={isLoading && !data}
            error={!data ? error : undefined}
            empty={Boolean(data && data.items.length === 0)}
            emptyText={search.q ? `No teams match “${search.q}”.` : 'No teams match these filters.'}
          />
          {data?.items.map((t) => <TeamRow key={t.id} t={t} />)}
        </tbody>
      </TableShell>

      {data ? (
        <Pagination total={data.total} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => setSearch({ offset: o || undefined })} />
      ) : null}
    </AdminLayout>
  );
}

function TeamRow({ t }: { t: AdminTeam }) {
  return (
    <tr className={TR}>
      <td className={TD}>
        <Link
          to="/teams/$id"
          params={{ id: t.id }}
          className="block rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        >
          <div className="font-medium text-neutral-100 hover:text-orange-300">{t.name}</div>
        </Link>
        <div className="flex flex-wrap items-center gap-1 text-xs text-neutral-500">
          /{t.slug}
          {t.kind === 'personal' ? <Badge tone="neutral">personal</Badge> : null}
          {t.archivedAt ? <Badge tone="warn">archived</Badge> : null}
          <span className="hidden sm:inline">
            <CopyId value={t.id} short label="team id" />
          </span>
        </div>
      </td>
      <td className={TD}>
        <div className="text-neutral-100">{t.billingPlan}</div>
        <div className="text-xs text-neutral-500">{t.subscriptionStatus}</div>
      </td>
      <td className={`${TD} text-right tabular-nums text-neutral-200`}>{t.memberCount}</td>
      <td className={`${TD} ${WIDE} text-right tabular-nums text-neutral-200`}>{fmtBytes(t.storageUsedBytes)}</td>
      <td className={`${TD} ${WIDE} whitespace-nowrap text-neutral-400`}>{fmtDate(t.currentPeriodEnd)}</td>
      <td className={`${TD} ${WIDE} whitespace-nowrap text-neutral-400`}>{fmtDate(t.createdAt)}</td>
    </tr>
  );
}
